import {RouteRecordRaw} from 'vue-router';
import {PrintTemplateType} from '../../../records/print-template.record';
import {Role} from '../../../records/user.record';
import driverFormRoutes from './driver-form.routes';
import vehicleFormRoutes from './vehicle-form.routes';
import {SettingsRecord} from '../../../records/settings.record';

export enum SettingsGroup {
    RESOURCES = 'RESOURCES ',
    CONFIGURATION = 'CONFIGURATION',
    EXECUTION = 'EXECUTION',
    COMMUNICATIONS = 'COMMUNICATIONS'
}

export default <RouteRecordRaw[]>[
    {
        path: 'account',
        name: 'aboutCompany',
        component: () => import('../../settings/about-company/AboutCompany.vue'),
        meta: {
            group: SettingsGroup.RESOURCES,
            roles: [Role.ADMIN]
        }
    },
    {
        path: 'distribution-centres',
        name: 'distributionCentres',
        component: () => import('../../settings/distribution-centres/DistributionCentresList.vue'),
        children: [
            {
                path: 'distribution-centres/:id',
                name: 'distributionCentreForm',
                component: () => import('../../settings/distribution-centres/DistributionCentreForm.vue'),
                props: (route) => {
                    return Object.assign({
                        id: route.params.id != 'add' ? route.params.id : null
                    });
                }
            }
        ],
        props: {},
        meta: {
            group: SettingsGroup.RESOURCES,
            roles: [Role.ADMIN, Role.DISPATCHER]
        }
    },
    {
        path: 'vehicles',
        name: 'vehicles',
        component: () => import('../../settings/vehicles/VehiclesList.vue'),
        props: {},
        children: vehicleFormRoutes('vehicles'),
        meta: {
            group: SettingsGroup.RESOURCES,
            roles: [Role.ADMIN, Role.DISPATCHER]
        }
    },
    {
        path: 'drivers',
        name: 'drivers',
        component: () => import('../../settings/drivers/DriversList.vue'),
        props: {},
        children: driverFormRoutes('drivers'),
        meta: {
            group: SettingsGroup.RESOURCES,
            roles: [Role.ADMIN, Role.DISPATCHER]
        }
    },
    {
        path: 'customerLocations',
        name: 'customerLocations',
        component: () => import('../../settings/customer-locations/CustomerLocationsList.vue'),
        props: {},
        meta: {
            group: SettingsGroup.RESOURCES,
            roles: [Role.ADMIN, Role.DISPATCHER]
        }
    },
    {
        path: 'users',
        name: 'users',
        component: () => import('../../settings/users/UsersList.vue'),
        children: [
            {
                path: 'users/:id',
                name: 'usersForm',
                component: () => import('../../settings/users/UserForm.vue'),
                props: (route) => {
                    return Object.assign({
                        id: route.params.id != 'add' ? route.params.id : null
                    });
                }
            }
        ],
        props: {},
        meta: {
            group: SettingsGroup.RESOURCES,
            roles: [Role.ADMIN]
        }
    },

    {
        path: 'appUsers',
        name: 'appUsers',
        component: () => import('../../settings/app-users/AppUsersList.vue'),
        children: [
            {
                path: 'appUsers/:id',
                name: 'appUsersForm',
                component: () => import('../../settings/app-users/AppUserForm.vue'),
                props: (route) => {
                    return Object.assign({
                        id: route.params.id != 'add' ? route.params.id : null
                    });
                }
            }
        ],
        props: {},
        meta: {
            group: SettingsGroup.RESOURCES,
            roles: [Role.ADMIN],
            access: (settings: SettingsRecord) => settings.appUsers
        }
    },
    {
        path: 'api-keys',
        name: 'apiKeys',
        component: () => import('../../settings/api-keys/ApiKeysList.vue'),
        props: {},
        meta: {
            group: SettingsGroup.RESOURCES,
            roles: [Role.ADMIN],
            access: (settings: SettingsRecord) => settings.enableAPIv6KeyManagementScreen
        }
    },
    {
        path: 'capacity-units',
        name: 'capacityUnitsTab',
        component: () => import('../../settings/account-settings/AccountSettings.vue'),
        props: {
            section: 'capacityUnits',
            group: SettingsGroup.CONFIGURATION
        },
        meta: {
            group: SettingsGroup.CONFIGURATION,
            roles: [Role.ADMIN]
        }
    },
    {
        path: 'localization',
        name: 'localisation',
        component: () => import('../../settings/account-settings/AccountSettings.vue'),
        props: {
            section: 'localisation',
            group: SettingsGroup.CONFIGURATION
        },
        meta: {
            group: SettingsGroup.CONFIGURATION,
            roles: [Role.ADMIN]
        }
    },
    {
        path: 'operation-duration',
        name: 'operationDuration',
        component: () => import('../../settings/account-settings/AccountSettings.vue'),
        props: {
            section: 'operationDuration',
            group: SettingsGroup.CONFIGURATION
        },
        meta: {
            group: SettingsGroup.CONFIGURATION,
            roles: [Role.ADMIN]
        }
    },
    {
        path: 'breaks',
        name: 'breaks',
        component: () => import('../../settings/account-settings/AccountSettings.vue'),
        props: {
            section: 'breaks',
            group: SettingsGroup.CONFIGURATION
        },
        meta: {
            group: SettingsGroup.CONFIGURATION,
            roles: [Role.ADMIN, Role.DISPATCHER],
            access: () => false
            // MD-3315 - breaks are completely hidden for 3.2.x+
        }
    },
    {
        path: 'territories',
        name: 'territories',
        component: () => import('../../settings/territories/Territories.vue'),
        meta: {
            group: SettingsGroup.CONFIGURATION,
            roles: [Role.ADMIN, Role.DISPATCHER],
            access: (settings: SettingsRecord) => settings.enableSchedulingZonesTab
        }
    },
    {
        path: 'vehicle-requirements',
        name: 'vehicleRequirements',
        component: () => import('../../settings/vehicle-requirements/VehicleRequirementsList.vue'),
        children: [
            {
                path: 'vehicle-requirements/:id',
                name: 'vehicleRequirementsForm',
                component: () => import('../../settings/vehicle-requirements/VehicleRequirementsForm.vue'),
                props: (route) => {
                    return Object.assign({
                        id: route.params.id != 'add' ? route.params.id : null
                    });
                }
            }
        ],
        meta: {
            group: SettingsGroup.CONFIGURATION,
            roles: [Role.ADMIN, Role.DISPATCHER]
        }
    },
    {
        path: 'vehicle-types',
        name: 'vehicleTypes',
        component: () => import('../../settings/vehicle-types/VehicleTypesList.vue'),
        children: [
            {
                path: 'vehicleTypes/:id',
                name: 'vehicleTypesForm',
                component: () => import('../../settings/vehicle-types/VehicleTypeForm.vue'),
                props: (route) => {
                    return Object.assign({
                        id: route.params.id != 'add' ? route.params.id : null
                    });
                }
            }
        ],
        meta: {
            group: SettingsGroup.CONFIGURATION,
            roles: [Role.ADMIN, Role.DISPATCHER],
            access: (settings: SettingsRecord) => settings.vehicleTypes
        }
    },
    {
        path: 'fuel-types',
        name: 'fuelTypes',
        component: () => import('../../settings/fuel-types/FuelTypesList.vue'),
        children: [
            {
                path: 'fuelTypes/:id',
                name: 'fuelTypesForm',
                component: () => import('../../settings/fuel-types/FuelTypeForm.vue'),
                props: (route) => {
                    return Object.assign({
                        id: route.params.id != 'add' ? route.params.id : null
                    });
                }
            }
        ],
        meta: {
            group: SettingsGroup.CONFIGURATION,
            roles: [Role.ADMIN],
            access: (settings: SettingsRecord) => settings.enableFuelPricesTab
        }
    },
    {
        path: 'speed-zones',
        name: 'speedZones',
        component: () => import('../../settings/speed-zones/SpeedZones.vue'),
        meta: {
            group: SettingsGroup.CONFIGURATION,
            roles: [Role.ADMIN, Role.DISPATCHER],
            access: (settings: SettingsRecord) => settings.enableSpeedZonesTab
        }
    },
    {
        path: 'planning-preferences',
        name: 'planningPreferences',
        component: () => import('../../settings/account-settings/AccountSettings.vue'),
        props: {
            section: 'planningPreferences',
            group: SettingsGroup.CONFIGURATION
        },
        meta: {
            group: SettingsGroup.CONFIGURATION,
            roles: [Role.ADMIN]
        }
    },
    {
        path: 'time-windows',
        name: 'timeWindows',
        component: () => import('../../settings/account-settings/AccountSettings.vue'),
        props: {
            section: 'timeWindows',
            group: SettingsGroup.CONFIGURATION
        },
        meta: {
            group: SettingsGroup.CONFIGURATION,
            roles: [Role.ADMIN]
        }
    },
    {
        path: 'import-export',
        name: 'importExport',
        component: () => import('../../settings/account-settings/AccountSettings.vue'),
        props: {
            section: 'importExport',
            group: SettingsGroup.CONFIGURATION
        },
        meta: {
            group: SettingsGroup.CONFIGURATION,
            roles: [Role.ADMIN, Role]
        }
    },
    {
        path: 'configuration',
        name: 'configuration',
        component: () => import('../../settings/account-settings/AccountSettings.vue'),
        props: {
            section: 'configuration',
            group: SettingsGroup.EXECUTION
        },
        meta: {
            group: SettingsGroup.EXECUTION,
            roles: [Role.ADMIN]
        }
    },
    {
        path: 'order-cancellation-reasons',
        name: 'orderCancellationReasons',
        component: () => import('../../settings/order-cancellation-reasons/OrderCancellationReasons.vue'),
        props: {
            section: 'configuration',
            group: SettingsGroup.EXECUTION
        },
        meta: {
            group: SettingsGroup.EXECUTION,
            roles: [Role.ADMIN]
        }
    },
    {
        path: 'order-item-cancellation-reasons',
        name: 'orderItemCancellationReasons',
        component: () => import('../../settings/order-item-cancellation-reasons/OrderItemCancellationReasons.vue'),
        props: {
            section: 'configuration',
            group: SettingsGroup.EXECUTION
        },
        meta: {
            group: SettingsGroup.EXECUTION,
            roles: [Role.ADMIN]
        }
    },
    {
        path: 'notifications',
        name: 'notifications',
        component: () => import('../../settings/account-settings/AccountSettings.vue'),
        props: {
            section: 'notifications',
            group: SettingsGroup.EXECUTION
        },
        meta: {
            group: SettingsGroup.EXECUTION,
            roles: [Role.ADMIN]
        }
    },
    {
        path: 'driver-manifest-conf',
        name: 'driverManifestConf',
        component: () => import('../../settings/driver-manifest-conf/DriverManifestConf.vue'),
        props: {
            type: PrintTemplateType.MANIFEST,
            title: 'driverManifest'
        },
        meta: {
            group: SettingsGroup.EXECUTION,
            roles: [Role.ADMIN],
            access: (settings) => !settings.useNewManifest
        }
    },
    {
        path: 'manifest-configuration',
        name: 'driverManifest',
        component: () => import('../../settings/templates-manager/TemplatesManager.vue'),
        props: {
            type: PrintTemplateType.MANIFEST,
            title: 'driverManifest'
        },
        meta: {
            group: SettingsGroup.EXECUTION,
            roles: [Role.ADMIN],
            access: (settings) => settings.useNewManifest
        }
    },
    {
        path: 'pod-configuration',
        name: 'pod',
        component: () => import('../../settings/templates-manager/TemplatesManager.vue'),
        props: {
            type: PrintTemplateType.POD,
            title: 'pod'
        },
        meta: {
            group: SettingsGroup.EXECUTION,
            roles: [Role.ADMIN]
        }
    },
    {
        path: 'vehicle-checks',
        name: 'vehicleChecks',
        component: () => import('../../settings/vehicle-checks/VehicleChecksList.vue'),
        children: [
            {
                path: 'vehicle-checks-form/:id/cloned=:cloned',
                name: 'VehicleCheckForm',
                component: () => import('../../settings/vehicle-checks/VehicleCheckForm.vue'),
                props: (route) => {
                    return Object.assign({
                        id: route.params.id != 'add' ? route.params.id : null,
                        cloned: route.params.cloned === 'false' ? false : true
                    });
                }
            }
        ],
        meta: {
            group: SettingsGroup.EXECUTION,
            roles: [Role.ADMIN],
            access: (settings: SettingsRecord) => settings.vehicleChecks
        }
    },
    {
        path: 'notification-centre',
        name: 'notificationCentre',
        component: () => import('../../settings/notification-centre/NotificationCentre.vue'),
        meta: {
            group: SettingsGroup.COMMUNICATIONS,
            roles: [Role.ADMIN]
        }
    },
    // Hide old notifications
    // {
    //     path: 'notification-parameters',
    //     name: 'notificationParameters',
    //     component: () => import('../../settings/notification-parameters/NotificationParametersList.vue'),
    //     meta: {
    //         group: SettingsGroup.COMMUNICATIONS,
    //         roles: [Role.ADMIN]
    //     }
    // },
    {
        path: 'smtp',
        name: 'smtp',
        component: () => import('../../settings/account-settings/MailSettings.vue'),
        props: {
            section: 'smtp',
            group: SettingsGroup.COMMUNICATIONS
        },
        meta: {
            group: SettingsGroup.COMMUNICATIONS,
            roles: [Role.ADMIN]
        }
    },
    {
        path: 'widget',
        name: 'widget',
        component: () => import('../../settings/account-settings/TrackingWidget.vue'),
        props: {
            section: 'widget',
            group: SettingsGroup.COMMUNICATIONS
        },
        meta: {
            group: SettingsGroup.COMMUNICATIONS,
            roles: [Role.ADMIN],
            access: (settings: SettingsRecord) => !settings.useNewWidgetService && settings.enableLocationWidget
        }
    },
    {
        path: 'website-widget',
        name: 'widgetPreview',
        component: () => import('../../settings/website-widget/WebsiteWidget.vue'),
        meta: {
            group: SettingsGroup.COMMUNICATIONS,
            roles: [Role.ADMIN],
            access: (settings: SettingsRecord) => !settings.useNewWidgetService && settings.enableLocationWidget
        }
    },
    {
        path: 'tracking-widget',
        name: 'trackingWidget',
        component: () => import('../../settings/tracking-widget/TrackingWidget.vue'),
        meta: {
            group: SettingsGroup.COMMUNICATIONS,
            roles: [Role.ADMIN],
            access: (settings: SettingsRecord) => settings.useNewWidgetService && settings.enableLocationWidget
        }
    }
];
